import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription, interval } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { ToastService } from '../../../../core/services/toast.service';
import { CoachingRealtimeService } from '../../services/coaching-realtime.service';
import { CoachMessage, CoachingService, MyCoach, ThreadDetail } from '../../services/coaching.service';

const THREAD_POLL_MS = 45000;
const MOBILE_BP = 768;

/**
 * End-user coaching messenger:
 * - Desktop: persistent right sidebar (always available)
 * - Phone: floating button → popup window
 */
@Component({
  selector: 'app-coaching-messenger',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './coaching-messenger.component.html',
  styleUrls: ['./coaching-messenger.component.scss']
})
export class CoachingMessengerComponent implements OnInit, OnDestroy {
  @ViewChild('messageList') messageList?: ElementRef<HTMLDivElement>;

  coaches: MyCoach[] = [];
  unread = 0;
  open = false;
  hideForRoute = false;
  isAuthed = false;
  isMobile = false;
  collapsed = false;
  refreshing = false;

  active: MyCoach | null = null;
  thread: ThreadDetail | null = null;
  loadingThread = false;
  draft = '';
  sending = false;

  private destroy$ = new Subject<void>();
  private poll?: Subscription;
  private myId: string | null = null;

  constructor(
    private realtime: CoachingRealtimeService,
    private coaching: CoachingService,
    private auth: AuthService,
    private toast: ToastService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.updateViewport();
    this.myId = this.auth.currentUserId;
    this.auth.currentUser$.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.isAuthed = !!user;
      this.myId = user?.id ?? null;
    });

    this.realtime.drawerOpen$.pipe(takeUntil(this.destroy$)).subscribe(open => {
      this.open = open;
      if (this.isMobile && !open) {
        this.poll?.unsubscribe();
        this.active = null;
        this.thread = null;
      }
    });

    this.realtime.userUnreadTotal$.pipe(takeUntil(this.destroy$)).subscribe(n => (this.unread = n));
    this.realtime.coaches$.pipe(takeUntil(this.destroy$)).subscribe(list => {
      this.coaches = list;
      if (this.active) {
        this.active = list.find(c => c.thread_id === this.active?.thread_id) || this.active;
      }
    });

    this.realtime.openThreadRequest$.pipe(takeUntil(this.destroy$)).subscribe(threadId => {
      const coach = this.coaches.find(c => c.thread_id === threadId);
      if (coach) {
        if (this.isMobile) {
          this.realtime.openDrawer(threadId);
        }
        this.openThread(coach);
      }
    });

    this.realtime.messages$.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (!this.active?.thread_id || event.thread_id !== this.active.thread_id || !this.thread) {
        return;
      }
      if (this.appendMessage(event.message)) {
        this.scrollToBottom();
        this.realtime.clearThreadUnread(event.thread_id);
      }
    });

    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(e => this.updateRouteVisibility(e.urlAfterRedirects));
    this.updateRouteVisibility(this.router.url);

    try {
      this.collapsed = localStorage.getItem('coachMessengerCollapsed') === 'true';
    } catch { /* ignore */ }
  }

  ngOnDestroy(): void {
    this.poll?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  updateViewport(): void {
    this.isMobile = window.innerWidth < MOBILE_BP;
  }

  get hasCoaches(): boolean {
    return this.coaches.some(c => !!c.thread_id);
  }

  get showShell(): boolean {
    // Admins/trainers use the global unread dock + coaching inbox instead.
    if (this.isStaffCoach) {
      return false;
    }
    return this.isAuthed && this.hasCoaches && !this.hideForRoute;
  }

  private get isStaffCoach(): boolean {
    const roles = this.auth.getRoles().map(r => String(r).toUpperCase());
    return roles.includes('ADMIN') || roles.includes('TRAINER');
  }

  /** Mobile FAB only */
  get showFab(): boolean {
    return this.showShell && this.isMobile;
  }

  /** Desktop dock always (unless collapsed to strip) */
  get showDesktopDock(): boolean {
    return this.showShell && !this.isMobile;
  }

  /** Expanded panel: desktop when not collapsed, or mobile when open */
  get showPanel(): boolean {
    if (!this.showShell) {
      return false;
    }
    if (this.isMobile) {
      return this.open;
    }
    return !this.collapsed;
  }

  toggle(): void {
    if (this.isMobile) {
      this.realtime.toggleDrawer();
      if (this.realtime.drawerOpen) {
        this.realtime.refreshCoaches();
      }
      return;
    }
    this.collapsed = !this.collapsed;
    try {
      localStorage.setItem('coachMessengerCollapsed', String(this.collapsed));
    } catch { /* ignore */ }
    if (!this.collapsed) {
      this.realtime.refreshCoaches();
    }
  }

  close(): void {
    if (this.isMobile) {
      this.realtime.closeDrawer();
      return;
    }
    this.collapsed = true;
    try {
      localStorage.setItem('coachMessengerCollapsed', 'true');
    } catch { /* ignore */ }
  }

  refresh(): void {
    this.refreshing = true;
    this.realtime.refreshCoaches();
    if (this.active?.thread_id) {
      this.fetch(this.active.thread_id, false);
    }
    setTimeout(() => (this.refreshing = false), 600);
  }

  openThread(coach: MyCoach): void {
    if (!coach.thread_id) {
      return;
    }
    if (this.active?.thread_id === coach.thread_id && this.thread) {
      return;
    }
    this.active = coach;
    this.thread = null;
    this.draft = '';
    this.loadingThread = true;
    this.realtime.setFocusedThread(coach.thread_id);
    this.fetch(coach.thread_id, true);

    this.poll?.unsubscribe();
    this.poll = interval(THREAD_POLL_MS).pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!document.hidden && this.active?.thread_id && !this.realtime.socketConnected) {
        this.fetch(this.active.thread_id, false);
      }
    });
  }

  backToList(): void {
    this.poll?.unsubscribe();
    this.active = null;
    this.thread = null;
    this.realtime.setFocusedThread(null);
    this.realtime.refreshCoaches();
  }

  isOutgoing(senderId: string, fromUser?: boolean): boolean {
    if (this.myId) {
      return senderId === this.myId;
    }
    return !!fromUser;
  }

  send(): void {
    const content = this.draft.trim();
    if (!content || !this.active?.thread_id || this.sending) {
      return;
    }
    const threadId = this.active.thread_id;
    this.sending = true;
    this.coaching.sendToMyCoach(threadId, content).subscribe({
      next: message => {
        this.sending = false;
        this.draft = '';
        if (this.active?.thread_id === threadId && this.appendMessage(message)) {
          this.scrollToBottom();
        }
      },
      error: err => {
        this.sending = false;
        this.toast.showError(err?.error?.message || 'Message not sent');
      }
    });
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  isSameDay(a?: string, b?: string): boolean {
    return !!a && !!b && new Date(a).toDateString() === new Date(b).toDateString();
  }

  initials(name?: string | null): string {
    return (name || '?').split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
  }

  /** Append if not already present (HTTP send + socket often race with the same id). */
  private appendMessage(message: CoachMessage): boolean {
    if (!this.thread || !message?.id) {
      return false;
    }
    if (this.thread.messages.some(m => m.id === message.id)) {
      return false;
    }
    this.thread = { ...this.thread, messages: [...this.thread.messages, message] };
    return true;
  }

  private fetch(threadId: string, initial: boolean): void {
    this.coaching.getMyCoachThread(threadId).pipe(takeUntil(this.destroy$)).subscribe({
      next: detail => {
        if (this.active?.thread_id !== threadId) {
          return;
        }
        const hadNew = !this.thread || detail.messages.at(-1)?.id !== this.thread.messages.at(-1)?.id;
        this.thread = detail;
        this.loadingThread = false;
        this.realtime.clearThreadUnread(threadId);
        if (hadNew) {
          this.scrollToBottom();
        }
      },
      error: () => {
        this.loadingThread = false;
        if (initial) {
          this.toast.showError('Could not open the conversation');
        }
      }
    });
  }

  private updateRouteVisibility(url: string): void {
    const path = url.split('?')[0].split('#')[0];
    this.hideForRoute =
      path.startsWith('/coach-console') ||
      path.includes('/manage/coaching') ||
      path.includes('/admin/coaching') ||
      path.includes('/manage-trainer/coaching') ||
      path.startsWith('/auth') ||
      path.startsWith('/onboarding') ||
      path.startsWith('/subscription');
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.messageList?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }
}
